import type { DepositAccount } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { capitalize } from 'lodash';
import * as React from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    Icon,
    Private,
    RowList,
    TextRow,
    View,
    YStack,
} from 'smoothie';

import { useSendEmailToSupport } from '../../../../common/hooks/useSendEmailToSupport';
import {
    centsToFormattedDollars,
    getNormalizedDepositAccountNickname,
    getTransactionIcon,
} from '../../../../common/utils/bankingUtils';
import {
    formatInTimezone,
    formatWithoutTimezone,
    SHORT_READABLE_DATE_FORMAT,
    TIME_FORMAT_WITH_TIMEZONE,
} from '../../../../common/utils/dateUtils';
import { selectedTransactionV2Atom } from '../../../state/SpacesAtoms';
import type { BankAccountDisplayDetail } from '../../../types/BankAccount';
import {
    TransactionDcSign,
    TransactionType,
} from '../../../types/Transactions';
import { TransactionIcons } from './TransactionIcons';

const TRANSACTION_DETAILS_ROW_TEXT_PROPS = {
    numberOfLines: 1,
    ellipsizeMode: 'tail' as const,
};

const getFormattedTransactionDateAndTime = (
    date?: string,
): { formattedDate?: string; formattedTime?: string } => {
    const formattedDate =
        date != null
            ? formatInTimezone(date, SHORT_READABLE_DATE_FORMAT)
            : undefined;
    const formattedTime =
        date != null
            ? formatInTimezone(date, TIME_FORMAT_WITH_TIMEZONE)
            : undefined;

    return { formattedDate, formattedTime };
};

const getPurchasedBy = (
    account: BankAccountDisplayDetail,
    cardId?: string,
): { value?: string; subtext?: string } => {
    const card =
        cardId != null
            ? account?.cards?.find(({ id }) => id === cardId)
            : undefined;

    return {
        value: card?.embossName?.line1,
        subtext:
            card?.lastFour != null
                ? `${capitalize(card.form)} card *${card.lastFour}`
                : undefined,
    };
};

const TransactionDetails: ({
    account,
}: {
    account: BankAccountDisplayDetail | null;
}) => React.JSX.Element | null = ({ account }) => {
    const sendEmailToSupport = useSendEmailToSupport();
    const selectedTransactionInfo = useAtomValue(selectedTransactionV2Atom);
    const { transaction } = selectedTransactionInfo ?? {
        transaction: null,
        syncteraAccountId: '',
    };

    if (account == null || transaction == null) {
        return null;
    }

    const {
        uuid,
        type: transactionType,
        subtype,
        status,
        transactionTime,
        postedDate,
        dcSign,
        amount,
        description,
        cardId,
        statementDescription,
        merchantCategory,
        formattedType,
        formattedTypeSubtext,
        enhancedMerchantName,
        pairedDepositAccount,
        primaryDepositAccount,
        isPayment,
        holdExpirationDate,
    } = transaction;

    const isPending = status === 'pending';

    const isCardTransaction =
        transactionType === TransactionType.CARD.toLowerCase();

    // Used to signify if we show a to/from field in the transaction details
    const hasPairedCustomerAccount =
        (transactionType === TransactionType.ACH.toLowerCase() ||
            (transactionType === 'internal_transfer' &&
                subtype === 'account_to_account')) &&
        pairedDepositAccount != null; /* ACH reversals will not show to/from */
    const pairedAccountIsFromAccount =
        dcSign === TransactionDcSign.CREDIT.toLowerCase();

    let toFromString = 'To';
    let toFromPrimaryText = 'External bank';
    let toFromSubtext = '';
    if (hasPairedCustomerAccount) {
        if (pairedAccountIsFromAccount) {
            toFromString = 'From';
        } else {
            toFromString = 'To';
        }
        if (pairedDepositAccount != null) {
            toFromPrimaryText =
                getNormalizedDepositAccountNickname(pairedDepositAccount);
            toFromSubtext = `${pairedDepositAccount.bankName} *${pairedDepositAccount.accountNumberMasked}`;
        }
    }

    let fromDepositAccount: DepositAccount | undefined;
    let toDepositAccount: DepositAccount | undefined;
    if (
        pairedDepositAccount !== undefined &&
        primaryDepositAccount !== undefined
    ) {
        if (pairedAccountIsFromAccount) {
            fromDepositAccount = pairedDepositAccount;
            toDepositAccount = primaryDepositAccount;
        } else {
            fromDepositAccount = primaryDepositAccount;
            toDepositAccount = pairedDepositAccount;
        }
    }

    const formattedAmount = `${dcSign === TransactionDcSign.CREDIT.toLowerCase() ? '+' : '-'}${centsToFormattedDollars(amount)}`;

    const {
        formattedDate: formattedTransactionDate,
        formattedTime: formattedTransactionTime,
    } = getFormattedTransactionDateAndTime(transactionTime);

    const formattedPostedDate: string | undefined =
        postedDate != null
            ? formatWithoutTimezone(postedDate, SHORT_READABLE_DATE_FORMAT)
            : undefined;

    const transactionIcon = getTransactionIcon(transaction);

    // Card-type dispute related transaction subtypes
    const isCardTypeDisputeRelatedTransaction =
        subtype === 'provisional_credit' ||
        subtype === 'provisional_credit_reversal' ||
        subtype === 'card_chargeback_case1' ||
        subtype === 'card_chargeback_case2' ||
        subtype === 'card_chargeback_case3';

    const isDisputeTransaction = transactionType === 'dispute';

    const showDisputeButton =
        transactionType !== 'internal_transfer' &&
        transactionType !== 'reward' &&
        transactionType !== 'fee' &&
        !isDisputeTransaction &&
        !isCardTypeDisputeRelatedTransaction &&
        !isPayment; // ENG-3225 do not show dispute button for card payments

    const isIncomingACHTransferFromExternalAccount =
        formattedTypeSubtext === TransactionType.ACH &&
        subtype === 'outgoing_debit';

    const {
        formattedDate: formattedHoldExpirationDate,
        formattedTime: formattedHoldExpirationTime,
    } = getFormattedTransactionDateAndTime(holdExpirationDate);

    if (account == null) {
        return <FullScreenSpinner />;
    }

    const purchasedByInfo = getPurchasedBy(account, cardId);

    return (
        <>
            <YStack gap={16}>
                <View alignItems="center" justifyContent="center">
                    {hasPairedCustomerAccount ? (
                        <TransactionIcons
                            fromAccount={fromDepositAccount}
                            toAccount={toDepositAccount}
                        />
                    ) : (
                        <YStack
                            backgroundColor="$gray100"
                            height={64}
                            width={64}
                            borderRadius={20}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Icon
                                icon={transactionIcon}
                                size={24}
                                color="$gray1000"
                            />
                        </YStack>
                    )}
                </View>
                <RowList>
                    <TextRow
                        title="Amount"
                        secondaryText={formattedAmount}
                        secondaryTextProps={TRANSACTION_DETAILS_ROW_TEXT_PROPS}
                        secondarySubtext={
                            isPending
                                ? 'Pending'
                                : subtype === 'atm_withdrawal'
                                  ? 'Includes ATM fees'
                                  : undefined
                        }
                        secondarySubtextProps={
                            TRANSACTION_DETAILS_ROW_TEXT_PROPS
                        }
                        isPrivate
                    />
                    <TextRow
                        title="Type"
                        secondaryText={formattedType}
                        secondaryTextProps={TRANSACTION_DETAILS_ROW_TEXT_PROPS}
                        secondarySubtext={formattedTypeSubtext ?? undefined}
                        secondarySubtextProps={
                            TRANSACTION_DETAILS_ROW_TEXT_PROPS
                        }
                    />
                    {hasPairedCustomerAccount && (
                        <TextRow
                            title={toFromString}
                            secondaryText={toFromPrimaryText}
                            secondaryTextProps={
                                TRANSACTION_DETAILS_ROW_TEXT_PROPS
                            }
                            secondarySubtext={toFromSubtext ?? undefined}
                            secondarySubtextProps={
                                TRANSACTION_DETAILS_ROW_TEXT_PROPS
                            }
                        />
                    )}
                    {isCardTransaction && (
                        <TextRow
                            title="Merchant"
                            secondaryText={
                                enhancedMerchantName ?? description ?? ''
                            }
                            secondaryTextProps={
                                TRANSACTION_DETAILS_ROW_TEXT_PROPS
                            }
                        />
                    )}
                    {formattedTransactionDate != null && (
                        <TextRow
                            title="Transaction date"
                            secondaryText={formattedTransactionDate}
                            secondaryTextProps={
                                TRANSACTION_DETAILS_ROW_TEXT_PROPS
                            }
                            secondarySubtext={
                                formattedTransactionTime ?? undefined
                            }
                            secondarySubtextProps={
                                TRANSACTION_DETAILS_ROW_TEXT_PROPS
                            }
                        />
                    )}
                    {/* only show expected arrival for incoming ACH transfers
                    from an external account */}
                    {holdExpirationDate != null &&
                        isPending &&
                        isIncomingACHTransferFromExternalAccount && (
                            <TextRow
                                title="Expected arrival"
                                secondaryText={
                                    formattedHoldExpirationDate ?? ''
                                }
                                secondaryTextProps={
                                    TRANSACTION_DETAILS_ROW_TEXT_PROPS
                                }
                                secondarySubtext={formattedHoldExpirationTime}
                                secondarySubtextProps={
                                    TRANSACTION_DETAILS_ROW_TEXT_PROPS
                                }
                            />
                        )}
                    {formattedPostedDate != null && (
                        <TextRow
                            title="Posted on"
                            secondaryText={formattedPostedDate}
                            secondaryTextProps={
                                TRANSACTION_DETAILS_ROW_TEXT_PROPS
                            }
                        />
                    )}
                    {purchasedByInfo.value != null &&
                        subtype !== 'atm_withdrawal' &&
                        !isDisputeTransaction && (
                            <TextRow
                                title="Purchased by"
                                secondaryText={purchasedByInfo.value}
                                secondaryTextProps={
                                    TRANSACTION_DETAILS_ROW_TEXT_PROPS
                                }
                                secondarySubtext={
                                    purchasedByInfo.subtext ?? undefined
                                }
                                secondarySubtextProps={
                                    TRANSACTION_DETAILS_ROW_TEXT_PROPS
                                }
                            />
                        )}
                    {isCardTransaction &&
                        subtype !== 'atm_withdrawal' &&
                        !isCardTypeDisputeRelatedTransaction &&
                        !isDisputeTransaction && (
                            <TextRow
                                title="Category"
                                secondaryText={
                                    merchantCategory ?? 'Uncategorized'
                                }
                                secondaryTextProps={
                                    TRANSACTION_DETAILS_ROW_TEXT_PROPS
                                }
                            />
                        )}
                </RowList>
                <YStack
                    backgroundColor="$gray200"
                    padding={16}
                    gap={4}
                    borderRadius={24}
                >
                    <BodySmallRegular color="$gray1000">
                        Appears on your statement as
                    </BodySmallRegular>
                    <Private>
                        <BodyMediumRegular>
                            {statementDescription ?? description}
                        </BodyMediumRegular>
                    </Private>
                </YStack>
            </YStack>
            {showDisputeButton && (
                <YStack gap={16}>
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.LARGE}
                        buttonProps={{ marginTop: 24 }}
                        text="Dispute this transaction"
                        disabled={isPending}
                        onPress={() => {
                            void sendEmailToSupport({
                                subject: `Transaction Dispute (ID ${uuid})`,
                            });
                        }}
                    />
                    {isPending && (
                        <BodySmallRegular color="$gray1000">
                            Pending transactions can&apos;t be disputed until
                            they post to the account.
                        </BodySmallRegular>
                    )}
                </YStack>
            )}
        </>
    );
};

export default TransactionDetails;
